import androidx.compose.runtime.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

@Composable
fun HomeScreen(
    season: SeasonDto?,
    leagues: List<SeasonLeagueDto>,
    error: String?,
    loading: Boolean,
    showNews: Boolean = true,
    onOverviewLoaded: (PublicSeasonOverviewDto) -> Unit = {},
) {
    var overview by remember(season?.id) { mutableStateOf<PublicSeasonOverviewDto?>(null) }
    var loadError by remember(season?.id) { mutableStateOf<String?>(null) }
    var overviewLoading by remember(season?.id) { mutableStateOf(true) }
    // The local Edge Function runtime can still be coming up when the browser gets its first
    // request. Keep the loading state through one retry rather than freezing a transient DNS
    // failure on the page until the user reloads it.
    var overviewLoadAttempt by remember(season?.id) { mutableStateOf(0) }

    LaunchedEffect(season?.id, loading, overviewLoadAttempt) {
        // The parent is still assembling the season-wide data.  Do not start a partial overview
        // request only to cancel it as soon as the league list arrives.
        if (loading) {
            overviewLoading = true
            loadError = null
            return@LaunchedEffect
        }

        val currentSeason = season
        if (currentSeason == null) {
            overviewLoading = false
            return@LaunchedEffect
        }

        overviewLoading = true
        loadError = null
        try {
            overview = Api.fetchPublicSeasonOverview(currentSeason.id).also(onOverviewLoaded)
        } catch (e: Throwable) {
            // A changed effect key cancels the previous request while the app is refreshing its
            // season data. Cancellation is expected, not a failed overview load.
            if (e is CancellationException) throw e
            if (overviewLoadAttempt == 0) {
                delay(500)
                overviewLoadAttempt++
            } else {
                loadError = "Načtení přehledu selhalo: ${e.apiMessage()}"
            }
        } finally {
            overviewLoading = false
        }
    }

    Div {
        Div(attrs = { classes("hero") }) {
            Div(attrs = { classes("hero-text") }) {
                H1 { Text(season?.let { "Gambrinus Bowlingliga - ${it.name}" } ?: "Gambrinus Bowlingliga") }
                P(attrs = { classes("hero-tagline") }) { Text("Stejné dráhy, stejná parta, nové stránky.... Legenda pokračuje") }
                if (season == null) {
                    P { Text("Právě není žádná aktivní sezóna.") }
                } else {
                    Div(attrs = { classes("hero-stats") }) {
                        HeroStat(leagues.size.toString(), "ligy")
                        HeroStat(overview?.teamCount?.toString() ?: "…", "týmů")
                        HeroStat(overview?.activePlayerCount?.toString() ?: "…", "aktivních hráčů")
                    }
                    overview?.let { summary ->
                        val roundInfo = remember(season.status, summary) {
                            computeRoundInfo(season.status == "finished", summary.roundSlots, summary.roundProgress)
                        }
                        key(season.id) {
                            RoundStatusSection(season.id, roundInfo, summary.roundProgress, summary.roundSlots, leagues)
                        }
                    }
                }
            }
            Picture(attrs = { classes("hero-art") }) {
                Source(attrs = {
                    attr("media", "(min-width: 721px)")
                    attr("type", "image/webp")
                    attr("srcset", "/gambrinus-theme-220.webp 1x, /gambrinus-theme-440.webp 2x, /gambrinus-theme-656.webp 3x")
                })
                Source(attrs = {
                    attr("media", "(min-width: 721px)")
                    attr("srcset", "/gambrinus-theme.png")
                })
                Img(src = "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=", alt = "Gambrinus Bowlingliga", attrs = {
                    classes("hero-image")
                    attr("width", "168"); attr("height", "220")
                    attr("fetchpriority", "high"); attr("decoding", "async")
                })
            }
        }

        if (loading || overviewLoading) {
            Div(attrs = { classes("card") }) { Text("Načítám…") }
        } else {
            error?.let { Div(attrs = { classes("card") }) { Text("Chyba: $it") } }
            loadError?.let {
                Div(attrs = { classes("card") }) {
                    Text("Chyba: $it")
                    Button(attrs = { onClick { overviewLoadAttempt++ } }) { Text("Zkusit načíst přehled znovu") }
                }
            }
        }

        // Aktuality jsou vázané na aktuální dění -- v archivu (dohraná sezóna) se nezobrazují.
        if (showNews) NewsSection()
    }
}

@Composable
private fun HeroStat(value: String, label: String) {
    Div(attrs = { classes("hero-stat") }) {
        B { Text(value) }
        Span { Text(label) }
    }
}

/**
 * What the hero's "Aktuálně se hraje" line should say, derived purely from the termínová listina
 * (season_round_slot, one calendar window per round_number shared by every league) and each
 * league's own matches. Doesn't distinguish phase -- a round_number's window is the same calendar
 * date for every league regardless of which phase (autumn/spring/nadstavba) that league considers
 * it, so "chybějící zápasy" are grouped by round_number alone, matching the termínová listina.
 */
private sealed interface RoundInfo {
    /** Usually a single round, but two SeasonRoundSlot windows can legitimately overlap (an
     * exclusive "kolo navíc" deliberately scheduled alongside a shared round, or just an
     * admin's manual date edit) -- every slot whose window contains today is "currently playing". */
    data class Playing(val roundNumbers: List<Int>, val makeupRounds: List<Int>) : RoundInfo
    data class Gap(val nextRoundNumber: Int, val nextRoundDate: String, val makeupRounds: List<Int>) : RoundInfo
    data class BeforeSeason(val firstRoundDate: String) : RoundInfo
    data class AfterSchedule(val makeupRounds: List<Int>) : RoundInfo
    data class Finished(val makeupRounds: List<Int>) : RoundInfo
    object NoData : RoundInfo
}

private fun computeRoundInfo(
    seasonFinished: Boolean,
    roundSlots: List<SeasonRoundSlotDto>,
    progress: List<RoundProgressDto>,
): RoundInfo {
    if (roundSlots.isEmpty()) return RoundInfo.NoData
    val sorted = roundSlots.sortedBy { it.roundNumber }

    fun fromEpoch(s: SeasonRoundSlotDto) = isoEpochDay(s.dateFrom)
    fun toEpoch(s: SeasonRoundSlotDto) = s.dateTo?.let { isoEpochDay(it) } ?: fromEpoch(s)
    fun incompleteRounds(predicate: (SeasonRoundSlotDto) -> Boolean): List<Int> =
        sorted.filter(predicate).map { it.roundNumber }
            .filter { rn -> progress.any { it.roundNumber == rn && it.incompleteCount > 0 } }

    if (seasonFinished) return RoundInfo.Finished(incompleteRounds { true })

    val today = isoEpochDay(todayIso())
    // Simultaneously active rounds are shown in the order in which they finish, not by their
    // round number. This also determines the order of their expandable match groups below.
    val current = sorted
        .filter { fromEpoch(it) <= today && today <= toEpoch(it) }
        .sortedWith(compareBy<SeasonRoundSlotDto> { toEpoch(it) }.thenBy { it.roundNumber })
    if (current.isNotEmpty()) {
        return RoundInfo.Playing(current.map { it.roundNumber }, incompleteRounds { toEpoch(it) < today })
    }

    val first = sorted.first()
    if (today < fromEpoch(first)) return RoundInfo.BeforeSeason(first.dateFrom)

    val last = sorted.last()
    if (today > toEpoch(last)) return RoundInfo.AfterSchedule(incompleteRounds { true })

    val next = sorted.first { fromEpoch(it) > today }
    return RoundInfo.Gap(next.roundNumber, next.dateFrom, incompleteRounds { toEpoch(it) < today })
}

private fun makeupSuffix(rounds: List<Int>): String =
    if (rounds.isEmpty()) "" else " (a dohrávky: ${rounds.joinToString(", ") { "$it. kolo" }})"

/** A round_number whose `SeasonRoundSlot` is exclusive to some leagues (spec 11.2 v1.53, "kolo
 * navíc") reads confusingly as a plain "N. kolo" -- other leagues in the season simply have no
 * match that round_number (their own next round comes later), which looks like a scheduling gap
 * unless the "vložené kolo" origin is spelled out, same wording as the public termínová listina. */
private fun exclusiveNote(roundNumber: Int, roundSlots: List<SeasonRoundSlotDto>, leagues: List<SeasonLeagueDto>): String {
    val slot = roundSlots.find { it.roundNumber == roundNumber } ?: return ""
    if (slot.leagueIds.isEmpty()) return ""
    val names = slot.leagueIds.map { lid -> leagues.find { it.id == lid }?.name ?: "?" }
    return " — vložené kolo jen pro: ${names.joinToString(", ")}"
}

/** Rozklikávací shrnutí "co se teď hraje" v hero sekci — rozkliknutí zobrazí zápasy aktuálního kola a chybějících dohrávek. */
private data class RoundGroup(val title: String, val roundNumber: Int, val onlyIncomplete: Boolean)

@Composable
private fun RoundStatusSection(
    seasonId: String,
    info: RoundInfo,
    progress: List<RoundProgressDto>,
    roundSlots: List<SeasonRoundSlotDto>,
    leagues: List<SeasonLeagueDto>,
) {
    if (info is RoundInfo.NoData) return
    var expanded by remember { mutableStateOf(false) }
    var matches by remember { mutableStateOf<List<MatchWithLeagueDto>>(emptyList()) }
    var teamNames by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var detailLoaded by remember { mutableStateOf(false) }
    var detailLoading by remember { mutableStateOf(false) }
    var detailError by remember { mutableStateOf<String?>(null) }
    var detailAttempt by remember { mutableStateOf(0) }
    fun teamName(id: String) = teamNames[id] ?: id

    fun matchesForRound(roundNumber: Int, onlyIncomplete: Boolean) =
        matches.filter { it.roundNumber == roundNumber && (!onlyIncomplete || it.status != "completed") }
            .sortedBy { it.seasonLeague?.name ?: "" }

    fun note(roundNumber: Int) = exclusiveNote(roundNumber, roundSlots, leagues)

    val headline: String
    val groups: List<RoundGroup>
    when (info) {
        is RoundInfo.Playing -> {
            headline = "Aktuálně se hraje " +
                info.roundNumbers.joinToString(", ") { "${it}. kolo${note(it)}" } +
                makeupSuffix(info.makeupRounds)
            groups = info.roundNumbers.map { RoundGroup("${it}. kolo${note(it)}", it, false) } +
                info.makeupRounds.map { RoundGroup("Dohrávka — $it. kolo${note(it)}", it, true) }
        }
        is RoundInfo.Gap -> {
            headline = "Pauza v rozpisu — příští kolo je ${info.nextRoundNumber}. od ${formatDate(info.nextRoundDate)}" +
                note(info.nextRoundNumber) + makeupSuffix(info.makeupRounds)
            groups = info.makeupRounds.map { RoundGroup("Dohrávka — $it. kolo${note(it)}", it, true) }
        }
        is RoundInfo.AfterSchedule -> {
            headline = "Odehrána všechna naplánovaná kola." + makeupSuffix(info.makeupRounds)
            groups = info.makeupRounds.map { RoundGroup("Dohrávka — $it. kolo${note(it)}", it, true) }
        }
        is RoundInfo.Finished -> {
            headline = "Sezóna skončila." + makeupSuffix(info.makeupRounds)
            groups = info.makeupRounds.map { RoundGroup("Dohrávka — $it. kolo${note(it)}", it, true) }
        }
        is RoundInfo.BeforeSeason -> {
            headline = "Sezóna začíná ${formatDate(info.firstRoundDate)} (1. kolo)."
            groups = emptyList()
        }
        RoundInfo.NoData -> return
    }

    val hasMatches = groups.any { group -> progress.any {
        it.roundNumber == group.roundNumber && (if (group.onlyIncomplete) it.incompleteCount else it.matchCount) > 0
    } }
    if (!hasMatches) {
        Div(attrs = { classes("hero-round") }) { Text(headline) }
        return
    }

    val roundNumbers = groups.map { it.roundNumber }.distinct()
    LaunchedEffect(expanded, roundNumbers, detailAttempt) {
        if (!expanded || detailLoaded) return@LaunchedEffect
        detailLoading = true
        detailError = null
        try {
            coroutineScope {
                val matchRequest = async { Api.fetchMatchesForSeason(null, seasonId, roundNumbers) }
                val teamRequest = async { Api.fetchAllTeamsForSeason(null, seasonId) }
                val fetchedMatches = matchRequest.await()
                val teams = teamRequest.await()
                matches = fetchedMatches
                teamNames = teams.associate { it.teamId to (it.teamNameOverride ?: it.team?.name ?: it.teamId) }
            }
            detailLoaded = true
        } catch (e: Throwable) {
            if (e is CancellationException) throw e
            detailError = "Načtení zápasů selhalo: ${e.apiMessage()}"
        } finally {
            detailLoading = false
        }
    }

    Div(attrs = {
        classes("hero-round", "clickable")
        onClick { expanded = !expanded }
    }) { Text((if (expanded) "▾ " else "▸ ") + headline) }

    if (expanded) {
        Div(attrs = { classes("hero-round-detail") }) {
            if (detailLoading) P { Text("Načítám zápasy…") }
            detailError?.let { message ->
                P { Text(message) }
                Button(attrs = { onClick { detailAttempt++ } }) { Text("Zkusit načíst zápasy znovu") }
            }
            groups.forEach { group ->
                val roundMatches = matchesForRound(group.roundNumber, group.onlyIncomplete)
                if (roundMatches.isNotEmpty()) {
                    H4 { Text(group.title) }
                    roundMatches.forEach { m ->
                        Div(attrs = { classes("hero-round-match") }) {
                            Span(attrs = { classes("hero-round-league") }) { Text(m.seasonLeague?.name ?: "") }
                            Span { Text("${teamName(m.homeTeamId)} – ${teamName(m.awayTeamId)}") }
                            if (m.status == "completed") {
                                Span(attrs = { classes("hero-round-score") }) { Text("${m.homeDuelPoints ?: 0}:${m.awayDuelPoints ?: 0}") }
                            }
                        }
                    }
                }
            }
        }
    }
}

/** Aktuality (spec 10.5): pinned first then newest, paginated in place via "Zobrazit starší". */
@Composable
private fun NewsSection() {
    var news by remember { mutableStateOf<List<NewsItemDto>>(emptyList()) }
    var offset by remember { mutableStateOf(0) }
    var hasMore by remember { mutableStateOf(false) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var attempt by remember { mutableStateOf(0) }

    LaunchedEffect(offset, attempt) {
        loading = true
        error = null
        try {
            val page = Api.fetchPublicNewsPage(offset)
            news = (if (offset == 0) page.items else news + page.items).distinctBy { it.id }
            hasMore = page.hasMore
        } catch (e: Throwable) {
            if (e is CancellationException) throw e
            error = "Načtení aktualit selhalo: ${e.apiMessage()}"
        } finally {
            loading = false
        }
    }

    if (!loading && error == null && news.isEmpty()) return

    Div(attrs = { classes("card") }) {
        H2 { Text("Aktuality") }
        news.forEach { item ->
            Div(attrs = { classes("news-item") }) {
                Div(attrs = { classes("news-head") }) {
                    if (item.isPinned) Span(attrs = { classes("news-pin") }) { Text("📌") }
                    Span(attrs = { classes("news-title") }) { Text(item.title) }
                    Span(attrs = { classes("news-date") }) { Text(formatDate(item.publishedAt?.substringBefore("T"))) }
                }
                RichContent(item.bodyHtml)
            }
        }
        error?.let { P { Text(it) } }
        if (loading) P { Text("Načítám…") }
        if (hasMore || error != null) {
            Button(attrs = {
                if (loading) disabled()
                onClick {
                    loading = true
                    if (error != null) attempt++ else offset += NEWS_PAGE_SIZE
                }
            }) { Text(if (error != null) "Zkusit znovu" else "Zobrazit starší") }
        }
    }
}
